import { useEffect, useCallback, useState } from "react";

import { useAuth, usePayment } from "../context";
import { useCredentials } from "../useCredentials/useCredentials.callback";

import { getAuthCookie, setAuthCookies } from "../../utils/cookies";

import { TArgs } from "./types";
import IndexedDb, { TABLE_BANKS } from "src/sync/indexedDb";

const REFRESH_CREDENTIALS_INTERVAL_LENGTH = 10 * 60 * 1000; // 10 minutes

export const useEstablish = async () => {
    const { authState, resetAuthState, updateAuthState } = useAuth();
    const { paymentState, updatePaymentState } = usePayment();
    const [localDatabase, setLocalDatabase] = useState<IndexedDb | undefined>(
        undefined
    );

    const { authCredentials } = useCredentials();

    const establish = useCallback(
        async ({ hasUpdateCredentialsCheck }: TArgs) => {
            // Credentials data
            if (hasUpdateCredentialsCheck) {
                if (
                    authState.updateCredentialsAt &&
                    new Date() >= authState.updateCredentialsAt
                ) {
                    resetAuthState();
                } else {
                    return;
                }
            }

            // *******************
            // *** Sync Engine ***
            // *******************
            // Initialise Sync Engine
            updatePaymentState({ paymentMethod: null });
            const localDb = new IndexedDb(`Dromedary-SyncEngine`);
            await localDb.createObjectStore([TABLE_BANKS]);
            setLocalDatabase(localDb);

            const lastUsedBank = await localDb.getValue(
                TABLE_BANKS,
                "lastUsedBank"
            );
            console.log("[useEstablish] syncEngine", { lastUsedBank });
            if (lastUsedBank && lastUsedBank.value) {
                updatePaymentState({
                    paymentMethod: lastUsedBank.value,
                    paymentUrl: null,
                });
            } else {
                updatePaymentState({ paymentMethod: undefined });
            }
            // *******************
            // *******************
            // *******************

            const { identityId, refreshToken } = getAuthCookie();

            console.log("getAuthCookie", { identityId, refreshToken });

            if (!refreshToken) {
                updateAuthState({ isLoggedIn: false, identityId });
                return;
            }

            const response = await authCredentials({ refreshToken });
            console.log("[useEstablish] authCredentials", { response });
            if (!response) return;

            setAuthCookies({
                identityId: response?.identityId ?? undefined,
                refreshToken: response?.refreshToken ?? undefined,
            });

            updateAuthState({
                ...response,
                identityId: response.identityId ?? undefined,
                narwhalId: response.narwhalId ?? undefined,
                credentials:
                    response.credentials && response.credentials !== null
                        ? JSON.parse(response.credentials)
                        : undefined,
                isLoggedIn: Boolean(response?.refreshToken),
                isGuest: false,
                isSignupComplete:
                    response.isSignupComplete === undefined ||
                    response.isSignupComplete === null
                        ? undefined
                        : response.isSignupComplete !== "false",
                intercomConfig: response.intercomConfig ?? undefined,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        async function saveLastUsedBank() {
            if (paymentState.paymentMethod?.id) {
                await localDatabase?.putValue(TABLE_BANKS, {
                    id: "lastUsedBank",
                    value: paymentState.paymentMethod,
                });
            }
        }
        saveLastUsedBank();
    }, [localDatabase, paymentState.paymentMethod]);

    useEffect(() => {
        establish({});

        let intervalId: NodeJS.Timeout;

        // helpers
        function clear() {
            clearInterval(intervalId);
        }

        function set() {
            clear();
            intervalId = setInterval(() => {
                establish({});
            }, REFRESH_CREDENTIALS_INTERVAL_LENGTH);
        }

        // call helpers depending on if the document is visible
        function visibilityListener() {
            switch (document.visibilityState) {
                case "hidden": {
                    clear();
                    break;
                }
                case "visible": {
                    establish({ hasUpdateCredentialsCheck: true });
                    set();
                    break;
                }
            }
        }

        set();

        document.addEventListener("visibilitychange", visibilityListener);

        return clear;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return establish;
};
